<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import playerService from '../services/player.service'
import categoryService from '../services/category.service'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || '')

// Form data
const form = ref({
  first_name: '',
  last_name: '',
  gender: '',
  birth_date: '',
  medical_exam_date: '',
  email: '',
  phone: '',
  license_number: '',
  photo: null as File | null,
  guardian1_first_name: '',
  guardian1_last_name: '',
  guardian2_first_name: '',
  guardian2_last_name: '',
  guardian_type: '',
  street: '',
  city: '',
  postal_code: '',
  school: ''
})

const selectedCategories = ref<any[]>([])
const availableCategories = ref<Array<{uuid: string, name: string}>>([])
const loading = ref(false)
const submitting = ref(false)

// Fetch categories
const fetchCategories = async () => {
  if (!clubUuid.value) return

  loading.value = true
  try {
    availableCategories.value = await categoryService.getCategoriesForFilters(clubUuid.value)
  } catch (err: any) {
    console.error('Error fetching categories:', err)
    toastStore.error(t('players.errorFetchingCategories'))
  } finally {
    loading.value = false
  }
}

// Custom label for multiselect
const customLabel = (option: any) => {
  return option.name || ''
}

// Crop image to square
const cropImageToSquare = (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Could not get canvas context'))
          return
        }

        // Calculate square size (minimum of width/height)
        const size = Math.min(img.width, img.height)
        canvas.width = size
        canvas.height = size

        // Calculate crop position (center crop)
        const offsetX = (img.width - size) / 2
        const offsetY = (img.height - size) / 2

        // Draw cropped image
        ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size)

        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            const croppedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now()
            })
            resolve(croppedFile)
          } else {
            reject(new Error('Could not create blob'))
          }
        }, file.type)
      }
      img.onerror = reject
      img.src = e.target?.result as string
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

// Handle file upload
const handleFileChange = async (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const file = target.files[0]

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      toastStore.error(t('players.errorSelectImage'))
      return
    }

    try {
      // Crop image to square
      form.value.photo = await cropImageToSquare(file)
    } catch (err) {
      console.error('Error cropping image:', err)
      toastStore.error(t('players.errorProcessingImage'))
    }
  }
}

// Submit form
const handleSubmit = async () => {
  if (!clubUuid.value) {
    toastStore.error(t('players.errorNoClubUuid'))
    return
  }

  // Validate required fields
  if (!form.value.first_name || !form.value.last_name || !form.value.gender) {
    toastStore.error(t('players.errorFillRequired'))
    return
  }

  submitting.value = true

  try {
    const formData = new FormData()

    // Required fields
    formData.append('first_name', form.value.first_name)
    formData.append('last_name', form.value.last_name)
    formData.append('gender', form.value.gender)

    // Optional fields
    if (form.value.birth_date) formData.append('birth_date', form.value.birth_date)
    if (selectedCategories.value.length > 0) {
      selectedCategories.value.forEach(category => formData.append('category_uuids[]', category.uuid))
    }
    if (form.value.medical_exam_date) formData.append('medical_exam_date', form.value.medical_exam_date)
    if (form.value.email) formData.append('email', form.value.email)
    if (form.value.phone) formData.append('phone', form.value.phone)
    if (form.value.license_number) formData.append('license_number', form.value.license_number)
    if (form.value.photo) formData.append('photo', form.value.photo)

    // Guardian fields
    if (form.value.guardian1_first_name) formData.append('guardian1_first_name', form.value.guardian1_first_name)
    if (form.value.guardian1_last_name) formData.append('guardian1_last_name', form.value.guardian1_last_name)
    if (form.value.guardian2_first_name) formData.append('guardian2_first_name', form.value.guardian2_first_name)
    if (form.value.guardian2_last_name) formData.append('guardian2_last_name', form.value.guardian2_last_name)
    if (form.value.guardian_type) formData.append('guardian_type', form.value.guardian_type)

    // Address fields
    if (form.value.street) formData.append('street', form.value.street)
    if (form.value.city) formData.append('city', form.value.city)
    if (form.value.postal_code) formData.append('postal_code', form.value.postal_code)
    if (form.value.school) formData.append('school', form.value.school)

    const response = await playerService.createPlayer(clubUuid.value, formData)

    toastStore.success(t('players.playerCreated'))
    router.push('/dashboard')
  } catch (err: any) {
    console.error('Error creating player:', err)
    const errorMessage = err.response?.data?.message || t('players.errorCreatingPlayer')
    toastStore.error(errorMessage)
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  fetchCategories()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('players.addPlayer') }}</h1>
      </div>

      <!-- Form -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="handleSubmit">
          <!-- Required Fields Section -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('players.requiredData') }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- First Name -->
              <div>
                <label for="firstName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.firstName') }} <span class="text-red-600">{{ t('common.required') }}</span>
                </label>
                <input
                  v-model="form.first_name"
                  type="text"
                  id="firstName"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Last Name -->
              <div>
                <label for="lastName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.lastName') }} <span class="text-red-600">{{ t('common.required') }}</span>
                </label>
                <input
                  v-model="form.last_name"
                  type="text"
                  id="lastName"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Gender -->
              <div>
                <label for="gender" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.gender') }} <span class="text-red-600">{{ t('common.required') }}</span>
                </label>
                <select
                  v-model="form.gender"
                  id="gender"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                >
                  <option value="">{{ t('players.selectGender') }}</option>
                  <option value="M">{{ t('players.male') }}</option>
                  <option value="W">{{ t('players.female') }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Optional Fields Section -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('players.optionalData') }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Birth Date -->
              <div>
                <label for="birthDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.birthDate') }}
                </label>
                <input
                  v-model="form.birth_date"
                  type="date"
                  id="birthDate"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Categories -->
              <div>
                <label for="categories" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.categories') }}
                </label>
                <Multiselect
                  v-model="selectedCategories"
                  :options="availableCategories"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  :placeholder="t('players.selectCategories')"
                  label="name"
                  track-by="uuid"
                  :custom-label="customLabel"
                  :loading="loading"
                />
              </div>

              <!-- Medical Exam Date -->
              <div>
                <label for="examDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.medicalExamDate') }}
                </label>
                <input
                  v-model="form.medical_exam_date"
                  type="date"
                  id="examDate"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Email -->
              <div>
                <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.email') }}
                </label>
                <input
                  v-model="form.email"
                  type="email"
                  id="email"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Phone Number -->
              <div>
                <label for="phone" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.phone') }}
                </label>
                <input
                  v-model="form.phone"
                  type="tel"
                  id="phone"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- License Number -->
              <div>
                <label for="license" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.licenseNumber') }}
                </label>
                <input
                  v-model="form.license_number"
                  type="text"
                  id="license"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Photo -->
              <div class="md:col-span-2">
                <label for="photo" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.photo') }}
                </label>
                <input
                  @change="handleFileChange"
                  type="file"
                  id="photo"
                  accept="image/*"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>
            </div>
          </div>

          <!-- Guardian/Parent Section -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('players.guardianData') }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- First Guardian First Name -->
              <div>
                <label for="guardian1FirstName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.guardian1FirstName') }}
                </label>
                <input
                  v-model="form.guardian1_first_name"
                  type="text"
                  id="guardian1FirstName"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- First Guardian Last Name -->
              <div>
                <label for="guardian1LastName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.guardian1LastName') }}
                </label>
                <input
                  v-model="form.guardian1_last_name"
                  type="text"
                  id="guardian1LastName"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Second Guardian First Name -->
              <div>
                <label for="guardian2FirstName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.guardian2FirstName') }}
                </label>
                <input
                  v-model="form.guardian2_first_name"
                  type="text"
                  id="guardian2FirstName"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Second Guardian Last Name -->
              <div>
                <label for="guardian2LastName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.guardian2LastName') }}
                </label>
                <input
                  v-model="form.guardian2_last_name"
                  type="text"
                  id="guardian2LastName"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Guardian Type -->
              <div>
                <label for="guardianType" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.guardianType') }}
                </label>
                <select
                  v-model="form.guardian_type"
                  id="guardianType"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                >
                  <option value="">{{ t('players.selectType') }}</option>
                  <option value="parent">{{ t('players.parent') }}</option>
                  <option value="guardian">{{ t('players.guardian') }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Address Section -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('players.addressData') }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Street -->
              <div class="md:col-span-2">
                <label for="street" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.street') }}
                </label>
                <input
                  v-model="form.street"
                  type="text"
                  id="street"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- City -->
              <div>
                <label for="city" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.city') }}
                </label>
                <input
                  v-model="form.city"
                  type="text"
                  id="city"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Postal Code -->
              <div>
                <label for="postalCode" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.postalCode') }}
                </label>
                <input
                  v-model="form.postal_code"
                  type="text"
                  id="postalCode"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- School -->
              <div class="md:col-span-2">
                <label for="school" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('players.school') }}
                </label>
                <input
                  v-model="form.school"
                  type="text"
                  id="school"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end gap-3">
            <button
              type="button"
              @click="router.push('/dashboard')"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
              :disabled="submitting"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="submitting"
            >
              {{ submitting ? t('players.adding') : t('players.addPlayer') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
